import axios from 'axios';
import uniqid from 'uniqid';
import { jwtDecode } from "jwt-decode";
import { Link, useLocation } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Estilos de react-toastify
import {
  FiUser, FiPhone, FiMail, FiGlobe, FiUploadCloud, FiFile,
  FiArrowRight, FiCode, FiPenTool, FiTrendingUp, FiShare2,
} from 'react-icons/fi';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import BASE_URL from '../../config/config';
import { cabecerasAuth, claves } from '../../queries/cliente';
import Verificacion from '../../components/Verificacion';
import Suspendido from '../../components/Suspendido';
import Loader from '../../components/Loader';
import RichTextEditor from '../../components/editor/RichTextEditor';
import { esHtmlVacio, sanitizarHtml } from '../../components/editor/sanitizarHtml';
import '../../css/nuevo-ticket.css';

// Icono fijo por departamento; label/descripcion se resuelven con t() al usarlos
const ICONOS_DEPARTAMENTO = { desarrollo: FiCode, diseno: FiPenTool, marketing: FiTrendingUp, redes_sociales: FiShare2 };
const CLAVES_DEPARTAMENTO = ['desarrollo', 'diseno', 'marketing', 'redes_sociales'];

const NuevoTicket = () => {
  const { t } = useTranslation();
  const DEPARTAMENTOS = CLAVES_DEPARTAMENTO.map((value) => ({
    value,
    label: t(`nuevoTicket.departamentos.${value}.label`),
    icon: ICONOS_DEPARTAMENTO[value],
    descripcion: t(`nuevoTicket.departamentos.${value}.descripcion`),
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [saliendo, setSaliendo] = useState(false);

  // Horario de oficina: mismo dato (y misma caché) que usa la portada. Si la
  // petición falla se calcula con los valores por defecto de siempre.
  const consultaHorario = useQuery({
    queryKey: claves.ajuste('horario'),
    queryFn: async () => (await axios.get(`${BASE_URL}/ajustes/horario`)).data?.ajuste?.valor || null,
    staleTime: 10 * 60_000,
  });

  const cargandoHorario = consultaHorario.isPending;

  const soporteDisponible = useMemo(() => {
    const horario = consultaHorario.data;
    const ahora = new Date();
    const dia = ahora.getDay(); // 0=Dom … 6=Sáb
    const horaDecimal = ahora.getHours() + ahora.getMinutes() / 60;

    const horaInicio = horario?.hora_inicio ?? 8;          // fija: 8:00
    const horaCierre = horario?.hora_cierre ?? 16;
    const horaCierreViernes = horario?.hora_cierre_viernes ?? 15;

    const esLaborable = dia >= 1 && dia <= 5;              // Lun–Vie
    const cierreHoy = dia === 5 ? horaCierreViernes : horaCierre;

    return esLaborable && horaDecimal >= horaInicio && horaDecimal < cierreHoy;
  }, [consultaHorario.data]);

  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [asunto, setAsunto] = useState('');
  const location = useLocation();
  const departmentFromState = location.state?.department || 'no_especificado'; // Recibir el departamento

  const [departamento, setDepartamento] = useState(departmentFromState); // Inicializar con el valor recibido

  const [prioridad, setPrioridad] = useState('no_urgente');
  const [usuarioDestinatario, setUsuarioDestinatario] = useState(null); // usuario al que se le abre el ticket
  const [busquedaDestinatario, setBusquedaDestinatario] = useState('');
  const [mostrarDropdownDestinatario, setMostrarDropdownDestinatario] = useState(false);
  const dropdownAsignacionRef = useRef(null);

  // Definición de tokenInfo
  const token = localStorage.getItem("AuthToken");
  let tokenInfo = {};
  if (token) {
    tokenInfo = jwtDecode(token);
  }

  // Clientes a los que un técnico/admin puede abrirles el ticket. Comparte
  // caché con el gestor de usuarios y se queda solo con los de rol "user".
  const { data: todosUsuarios = [] } = useQuery({
    queryKey: claves.usuarios(),
    queryFn: async () => (await axios.get(`${BASE_URL}/todos-usuarios`, cabecerasAuth())).data.usuarios || [],
    enabled: tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico',
    select: (usuarios) => usuarios.filter((u) => u.role === 'user'),
  });

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickFuera = (e) => {
      if (dropdownAsignacionRef.current && !dropdownAsignacionRef.current.contains(e.target)) {
        setMostrarDropdownDestinatario(false);
      }
    };
    document.addEventListener('mousedown', handleClickFuera);
    return () => document.removeEventListener('mousedown', handleClickFuera);
  }, [])

  // Usuarios filtrados por búsqueda (solo rol user)
  const usuariosFiltrados = useMemo(() =>
    todosUsuarios.filter(u =>
      `${u.nombre} ${u.apellidos} ${u.usuario}`.toLowerCase().includes(busquedaDestinatario.toLowerCase())
    ), [todosUsuarios, busquedaDestinatario]);

  const seleccionarDestinatario = (usuario) => {
    setUsuarioDestinatario(usuario);
    setBusquedaDestinatario('');
    setMostrarDropdownDestinatario(false);
  };

  const limpiarDestinatario = () => {
    setUsuarioDestinatario(null);
    setBusquedaDestinatario('');
  };

  if (tokenInfo.estado === 'verificar') return <Verificacion />;
  if (tokenInfo.estado === 'suspendido') return <Suspendido />;
  if (cargandoHorario) return <div className="loader-fullscreen"><Loader /></div>;

  const handleChange = (value) => {
    setContent(value);
  };

  const procesarArchivos = (files) => {
    const maxFileSize = 5 * 1024 * 1024; // 5MB en bytes
    const validFiles = [];

    for (let i = 0; i < files.length; i++) {
      if (files[i].size <= maxFileSize) {
        validFiles.push(files[i]);
      } else {
        toast.error(t('nuevoTicket.archivoExcede', { nombre: files[i].name }));
      }
    }

    // Se acumulan: los archivos pueden llegar del dropzone, del selector o del editor
    setAttachments((previos) => {
      const actuales = Array.from(previos || []);
      const nuevos = validFiles.filter(
        (file) => !actuales.some((existente) => existente.name === file.name && existente.size === file.size)
      );
      return [...actuales, ...nuevos];
    });
  };

  const handleAttachmentChange = (e) => {
    procesarArchivos(e.target.files);
    e.target.value = ''; // Permite volver a elegir el mismo archivo tras quitarlo
  };

  const handleDropArchivos = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      procesarArchivos(e.dataTransfer.files);
    }
  };

  const handleRemoveAttachment = (index) => {
    const updatedAttachments = [...attachments];
    updatedAttachments.splice(index, 1);
    setAttachments(updatedAttachments);
  };

  const handleAsuntoChange = (e) => {
    setAsunto(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (asunto.trim().length < 5) {
        toast.error(t('nuevoTicket.asuntoVacio'));
        return;
    }

    // El editor devuelve HTML: se sanea y se mide el texto visible, no las etiquetas
    const descripcion = sanitizarHtml(content);
    const textoDescripcion = new DOMParser()
        .parseFromString(`<body>${descripcion}</body>`, 'text/html')
        .body.textContent.replace(/\u00A0/g, ' ').trim();

    if (esHtmlVacio(descripcion) || textoDescripcion.length < 5) {
        toast.error(t('nuevoTicket.descripcionVacia'));
        return;
    }

    setIsSubmitting(true);
      // Validación de selección de departamento
      if (departamento === 'no_especificado') {
        toast.error(t('nuevoTicket.seleccionaDepartamento'));
        setIsSubmitting(false);
        return;
      }

    const identificador_unico = uniqid.time();
    const uniqid_up = identificador_unico.toUpperCase();

    // Si se abre el ticket en nombre de otro usuario, usar sus datos
    const creador = usuarioDestinatario || tokenInfo;

    const formData = new FormData();
    formData.append('identificador', uniqid_up);
    formData.append('nombre', creador.nombre);
    formData.append('apellidos', creador.apellidos);
    formData.append('telefono', creador.telefono);
    formData.append('email', creador.email);
    formData.append('webs_del_cliente', creador.webs_del_cliente);
    formData.append('asunto', asunto);
    formData.append('descripcion', descripcion);
    formData.append('dpto', departamento);
    formData.append('usuario_creador', creador.usuario);
    formData.append('abierto_por', tokenInfo.usuario);
    formData.append('prioridad', prioridad);

    for (let i = 0; i < attachments.length; i++) {
        formData.append('archivos', attachments[i]);
    }

    try {
        // Crear el ticket
        const response = await axios.post(`${BASE_URL}/crear-ticket`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Transición suave: se desvanece el formulario antes de navegar al ticket creado
        setSaliendo(true);
        await new Promise((resolve) => setTimeout(resolve, 280));
        navigate(`/ticket-creado/${response.data.identificador}`);

        // Llamar a la nueva ruta para notificar a administrador
        await axios.post(`${BASE_URL}/notificar-nuevo-ticket-admin`, {
            asunto: asunto,
            identificador: uniqid_up,
            usuario_creador: creador.usuario,
            descripcion: descripcion,
            nombre: creador.nombre,
            apellidos: creador.apellidos,
            dpto: departamento,
            // Si es un técnico abriendo en nombre de un usuario, se indica para usar plantilla distinta
            ...(usuarioDestinatario && {
                nombre_tecnico: tokenInfo.nombre,
                usuario_tecnico: tokenInfo.usuario
            })
        });

        await axios.post(`${BASE_URL}/notificar-nuevo-ticket-cliente`, {
          asunto: asunto,
          identificador: uniqid_up,
          usuario_creador: creador.usuario,
          descripcion: descripcion,
          email_cliente: creador.email,
          nombre_cliente: creador.nombre
      });

        // Si el ticket fue abierto en nombre de un usuario por un técnico/admin, notificar al destinatario
        if (usuarioDestinatario) {
          await axios.post(`${BASE_URL}/notificar-ticket-gestion`, {
            identificador: uniqid_up,
            asunto: asunto,
            descripcion: descripcion,
            dpto: departamento,
            nombre_destinatario: usuarioDestinatario.nombre,
            email_destinatario: usuarioDestinatario.email,
            nombre_tecnico: tokenInfo.nombre,
            usuario_tecnico: tokenInfo.usuario
          });
        }

    } catch (error) {
        toast.error(t('nuevoTicket.errorCrear'));
        console.error('Error al crear el ticket:', error);
        setIsSubmitting(false); // Desbloquear el botón si hay error
    }
  };

  const esStaff = tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico';
  const departamentoSeleccionado = DEPARTAMENTOS.find(d => d.value === departamento);

  return (
    <main className={`nuevo-ticket site-width ${saliendo ? 'nt-saliendo' : ''}`}>
      <div className="nt-layout">
        <aside className="nt-aside">
          <div className="nt-card nt-welcome-card">
            <h2>{t('nuevoTicket.hola', { nombre: tokenInfo.nombre })}</h2>
            {esStaff ? (
              <p>
                <Trans i18nKey="nuevoTicket.creandoComo" values={{ rol: tokenInfo.role === 'admin' ? t('nuevoTicket.administrador') : t('nuevoTicket.tecnico') }}>
                  Estás creando un ticket como <strong>rol</strong>. Puedes asignarlo directamente a cualquier usuario.
                </Trans>
              </p>
            ) : (
              <p>{t('nuevoTicket.rellenaFormulario')}</p>
            )}
          </div>

          <div className={`nt-availability-pill ${soporteDisponible ? 'is-open' : 'is-closed'}`}>
            <span className="nt-availability-dot" />
            {soporteDisponible ? t('nuevoTicket.soporteDisponibleAhora') : t('nuevoTicket.fueraHorario')}
          </div>
          {!soporteDisponible && (
            <p className="nt-availability-note">
              {esStaff
                ? t('nuevoTicket.notaOrdenLlegadaStaff')
                : t('nuevoTicket.notaOrdenLlegadaCliente')}
            </p>
          )}

          <div className="nt-card nt-contact-card">
            <div className="nt-card-header">
              <span>{t('nuevoTicket.tusDatos')}</span>
              <Link to="/dashboard/perfil" className="nt-edit-link">{t('nuevoTicket.editar')}</Link>
            </div>
            <p className="nt-card-hint">
              {esStaff
                ? t('nuevoTicket.hintStaff')
                : t('nuevoTicket.hintCliente')}
            </p>
            <div className="nt-contact-list">
              <div className="nt-contact-row">
                <FiUser />
                <div><span>{t('nuevoTicket.nombre')}</span><p>{tokenInfo.nombre} {tokenInfo.apellidos}</p></div>
              </div>
              <div className="nt-contact-row">
                <FiPhone />
                <div><span>{t('nuevoTicket.telefono')}</span><p>{tokenInfo.telefono || '—'}</p></div>
              </div>
              <div className="nt-contact-row">
                <FiMail />
                <div><span>{t('nuevoTicket.email')}</span><p>{tokenInfo.email}</p></div>
              </div>
              <div className="nt-contact-row">
                <FiGlobe />
                <div><span>{t('nuevoTicket.sitiosMantenimiento')}</span><p>{tokenInfo.webs_del_cliente || '—'}</p></div>
              </div>
            </div>
          </div>
        </aside>

        <section className="nt-card nt-form-card">
          <h3>{esStaff ? t('nuevoTicket.crearNuevoTicket') : t('nuevoTicket.aPuntoDeCrear')}</h3>

          {esStaff && (
            <div className="nt-section nt-assign-block">
              <label className="nt-label">{t('nuevoTicket.abrirAUsuario')} <span className='nt-optional'>{t('nuevoTicket.opcional')}</span></label>

              {usuarioDestinatario ? (
                <div className="asignacion-chip">
                  <div className="asignacion-chip-info">
                    <span className="asignacion-chip-nombre">{usuarioDestinatario.nombre} {usuarioDestinatario.apellidos}</span>
                    <span className="asignacion-chip-usuario">@{usuarioDestinatario.usuario}</span>
                    <span className="asignacion-chip-email">{usuarioDestinatario.email}</span>
                  </div>
                  <button type="button" className="asignacion-chip-quitar" onClick={limpiarDestinatario} title={t('nuevoTicket.quitar')}>&#10005;</button>
                </div>
              ) : (
                <div className="selector-usuario-busqueda" ref={dropdownAsignacionRef}>
                  <div className="campo-busqueda-usuario">
                    <input
                      type="text"
                      className="input-busqueda-usuario"
                      placeholder={t('nuevoTicket.buscarUsuario')}
                      value={busquedaDestinatario}
                      onChange={(e) => setBusquedaDestinatario(e.target.value)}
                      onFocus={() => setMostrarDropdownDestinatario(true)}
                    />
                    {busquedaDestinatario && (
                      <button type="button" className="boton-limpiar-usuario" onClick={() => setBusquedaDestinatario('')}>&#10005;</button>
                    )}
                  </div>

                  {mostrarDropdownDestinatario && (
                    <div className="lista-usuarios-desplegable">
                      {usuariosFiltrados.length > 0 ? (
                        usuariosFiltrados.map(u => (
                          <div key={u.usuario} className="item-usuario-desplegable" onClick={() => seleccionarDestinatario(u)}>
                            <div className="info-usuario-item">
                              <span className="nombre-usuario-item">{u.nombre} {u.apellidos}</span>
                              <span className="usuario-item">@{u.usuario}</span>
                            </div>
                            <span className="email-usuario-item">{u.email}</span>
                          </div>
                        ))
                      ) : (
                        <div className="sin-resultados-usuario">{t('nuevoTicket.sinUsuarios')}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="nt-section">
            <label className="nt-label">{t('nuevoTicket.preguntaDepartamento')} <span className='nt-required'>*</span></label>
            <div className="nt-dept-grid">
              {DEPARTAMENTOS.map((dept) => (
                <button
                  key={dept.value}
                  type="button"
                  className={`nt-dept-card nt-dept-card--${dept.value} ${departamento === dept.value ? 'active' : ''}`}
                  aria-pressed={departamento === dept.value}
                  onClick={() => setDepartamento(dept.value)}
                >
                  <dept.icon />
                  <span>{dept.label}</span>
                </button>
              ))}
            </div>
            {departamentoSeleccionado && (
              <p className={`nt-dept-desc nt-dept-desc--${departamentoSeleccionado.value}`}>{departamentoSeleccionado.descripcion}</p>
            )}
          </div>

          <div className="nt-section">
            <label className="nt-label" htmlFor="nt-asunto">{t('nuevoTicket.asunto')} <span className='nt-required'>*</span></label>
            <input id="nt-asunto" className="nt-input" type="text" placeholder={t('nuevoTicket.placeholderAsunto')} value={asunto} onChange={handleAsuntoChange} />
          </div>

          <div className="nt-section">
            <label className="nt-label">{t('nuevoTicket.describeDetalladamente')} <span className='nt-required'>*</span></label>
            <RichTextEditor
              className="nt-rte"
              value={content}
              onChange={handleChange}
              onFiles={procesarArchivos}
              placeholder={t('nuevoTicket.placeholderDescripcion')}
            />
          </div>

          <div className="nt-section">
            <label className="nt-label">{t('nuevoTicket.adjuntarArchivos')} <span className='nt-optional'>{t('nuevoTicket.opcional')}</span></label>
            <p className="nt-hint">
              <Trans i18nKey="nuevoTicket.hintArchivos">
                También puedes arrastrarlos o pegarlos directamente en la descripción. Si algún archivo pesa más de 5 MB, súbelo a <a href='https://wetransfer.com/' target='_blank' rel="noreferrer">WeTransfer</a> y pega el enlace en la descripción.
              </Trans>
            </p>
            <label
              htmlFor="nt-file-input"
              className={`nt-dropzone ${isDragging ? 'is-dragging' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDropArchivos}
            >
              <FiUploadCloud />
              <p>{t('nuevoTicket.arrastraArchivos')}</p>
              <small>{t('nuevoTicket.formatosArchivos')}</small>
              <input
                className="nt-dropzone-input"
                accept=".pdf,.doc,.docx,.jpg,.png,.jpeg,.webp,.pages,.html"
                name="text"
                id="nt-file-input"
                type="file"
                multiple
                onChange={handleAttachmentChange}
              />
            </label>
            {attachments && attachments.length > 0 && (
                <div className='nt-files-grid'>
                  {Array.from(attachments).map((file, index) => (
                    <div className='nt-file-chip' key={index}>
                      <FiFile />
                      <p>{file.name}</p>
                      <button type="button" className='nt-file-remove' onClick={() => handleRemoveAttachment(index)}>&#10005;</button>
                    </div>
                  ))}
                </div>
            )}
          </div>

          <div className="nt-section">
            <label className="nt-label">{t('nuevoTicket.esUrgente')}</label>
            <div className="nt-priority-toggle">
              <button
                type="button"
                className={prioridad === 'no_urgente' ? 'active' : ''}
                onClick={() => setPrioridad('no_urgente')}
              >
                {t('nuevoTicket.noUrgente')}
              </button>
              <button
                type="button"
                className={`nt-priority--urgente ${prioridad === 'urgente' ? 'active' : ''}`}
                onClick={() => setPrioridad('urgente')}
              >
                {t('nuevoTicket.urgente')}
              </button>
            </div>
          </div>

          <button
            className='nt-submit-btn'
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <><span className="btn-spinner" /> {t('nuevoTicket.enviando')}</>
            ) : (
              <>{t('nuevoTicket.enviarTicket')} <FiArrowRight /></>
            )}
          </button>
        </section>
      </div>
      <ToastContainer position="bottom-right" autoClose={3000} />
    </main>
  )
}

export default NuevoTicket;
